import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { adminApi as api } from "@/lib/api";
import { ExternalLink, Play, Plus, Trash2, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const emptySource = { municipality_name: "", source_url: "", source_type: "candidate_list", notes: "", active: true };

export default function AdminCandidateData() {
  const [candidates, setCandidates] = useState([]);
  const [filter, setFilter] = useState("pending_review");
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [running, setRunning] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptySource);

  const load = useCallback(() => {
    api.get("/admin/ingestion/candidates", { params: filter ? { review_status: filter } : {} })
      .then((r) => setCandidates(r.data)).catch(() => {});
    api.get("/admin/ingestion/sources").then((r) => setSources(r.data)).catch(() => {});
    api.get("/admin/ingestion/runs").then((r) => setRuns(r.data)).catch(() => {});
    api.get("/admin/system-alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/ingestion/run");
      toast.success(`Scan complete: ${r.data.stats.new_candidates} new, ${r.data.stats.updated_candidates} updated, ${r.data.errors.length} error(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setRunning(false);
    }
  };

  const review = async (id, status) => {
    try {
      await api.patch(`/admin/ingestion/candidates/${id}/review`, { review_status: status });
      toast.success(`Candidate ${status === "approved" ? "approved" : status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const saveSource = async () => {
    if (!form.municipality_name || !form.source_url) {
      toast.error("Municipality name and source URL are required");
      return;
    }
    try {
      await api.post("/admin/ingestion/sources", form);
      toast.success("Source added");
      setDialogOpen(false);
      setForm(emptySource);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save source");
    }
  };

  const deleteSource = async (id) => {
    try {
      await api.delete(`/admin/ingestion/sources/${id}`);
      toast.success("Source removed");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const resolveAlert = async (id) => {
    try {
      await api.patch(`/admin/system-alerts/${id}/resolve`);
      load();
    } catch {}
  };

  return (
    <div data-testid="admin-candidate-data">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground">Candidate Data</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ontario-wide candidate ingestion — runs automatically every day at 6:00 PM Eastern. Official sources only.
          </p>
        </div>
        <Button onClick={runNow} disabled={running} data-testid="ingestion-run-now">
          {running ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
          {running ? "Scanning…" : "Run scan now"}
        </Button>
      </div>

      {alerts.length > 0 && (
        <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="ingestion-alerts">
          <CardContent className="p-4 space-y-2">
            {alerts.map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-3 text-sm text-amber-900">
                <span className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {a.message}</span>
                <Button size="sm" variant="outline" onClick={() => resolveAlert(a.id)} data-testid={`resolve-alert-${a.id}`}>Resolve</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="queue">
        <TabsList>
          <TabsTrigger value="queue" data-testid="tab-queue">Review queue</TabsTrigger>
          <TabsTrigger value="sources" data-testid="tab-sources">Sources</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">Run history</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-4">
          <div className="flex items-center gap-3 mb-4">
            <Label className="text-sm">Status</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48 bg-white" data-testid="queue-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="pending_review">Pending review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="auto_listed">Auto listed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-3" data-testid="queue-list">
            {candidates.map((c) => (
              <Card key={c.id} data-testid={`queue-item-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-heading font-semibold text-sm text-foreground">{c.full_name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {c.municipality_name}{c.office ? ` · ${c.office}` : ""}{c.ward ? ` · ${c.ward}` : ""}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Badge variant="outline" className="text-[10px]">{c.confidence}</Badge>
                        <Badge variant="outline" className="text-[10px]">{c.verification_status}</Badge>
                        <a href={c.source_url} target="_blank" rel="noopener noreferrer"
                           className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                          Source <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                      {c.pending_changes && (
                        <p className="text-[11px] text-amber-700 mt-2">
                          Pending source changes: {JSON.stringify(c.pending_changes)}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {c.review_status !== "approved" && (
                        <Button size="sm" onClick={() => review(c.id, "approved")} data-testid={`approve-${c.id}`}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                        </Button>
                      )}
                      {c.review_status !== "rejected" && (
                        <Button size="sm" variant="outline" onClick={() => review(c.id, "rejected")} data-testid={`reject-${c.id}`}>
                          <XCircle className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {candidates.length === 0 && (
              <Card><CardContent className="p-6 text-sm text-muted-foreground" data-testid="queue-empty">
                No records with this status. New candidates found by the daily scan will appear here for review.
              </CardContent></Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="sources" className="mt-4">
          <div className="flex justify-end mb-4">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-source-button"><Plus className="h-4 w-4 mr-1.5" /> Add source</Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle className="font-heading">Add official source</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="src-muni" className="mb-1.5 block">Municipality name</Label>
                    <Input id="src-muni" value={form.municipality_name}
                           onChange={(e) => setForm({ ...form, municipality_name: e.target.value })}
                           data-testid="source-muni-input" placeholder="e.g. Kawartha Lakes" />
                  </div>
                  <div>
                    <Label htmlFor="src-url" className="mb-1.5 block">Official source URL</Label>
                    <Input id="src-url" value={form.source_url}
                           onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                           data-testid="source-url-input" placeholder="https://… (official municipal election page)" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Type</Label>
                    <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                      <SelectTrigger className="bg-white" data-testid="source-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="candidate_list">Candidate list (import candidates)</SelectItem>
                        <SelectItem value="election_news">Election page monitor (news drafts)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="src-notes" className="mb-1.5 block">Notes</Label>
                    <Textarea id="src-notes" value={form.notes}
                              onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="src-active" />
                    <Label htmlFor="src-active">Active</Label>
                  </div>
                  <Button onClick={saveSource} className="w-full" data-testid="source-save-button">Save source</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-3" data-testid="sources-list">
            {sources.map((s) => (
              <Card key={s.id} data-testid={`source-${s.id}`}>
                <CardContent className="p-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-heading font-semibold text-sm text-foreground">{s.municipality_name}</p>
                    <a href={s.source_url} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-0.5 break-all">
                      {s.source_url} <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <Badge variant="outline" className="text-[10px]">{s.source_type}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${
                        s.last_status === "ok" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : s.last_status === "error" ? "bg-red-50 text-red-800 border-red-200"
                        : s.last_status === "needs_review" ? "bg-amber-50 text-amber-800 border-amber-200"
                        : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                        {s.last_status}
                      </Badge>
                      {!s.active && <Badge variant="outline" className="text-[10px]">inactive</Badge>}
                      <span className="text-[11px] text-muted-foreground">
                        Last checked: {s.last_checked_at ? new Date(s.last_checked_at).toLocaleString("en-CA") : "never"}
                      </span>
                    </div>
                    {s.notes && <p className="text-[11px] text-muted-foreground mt-2 max-w-xl">{s.notes}</p>}
                    {s.last_error && <p className="text-[11px] text-red-700 mt-1">{s.last_error}</p>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => deleteSource(s.id)} aria-label="Delete source" data-testid={`delete-source-${s.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
            {sources.length === 0 && <p className="text-sm text-muted-foreground">No sources configured.</p>}
          </div>
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          <div className="space-y-3" data-testid="runs-list">
            {runs.map((r) => (
              <Card key={r.id}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-foreground">
                      {new Date(r.started_at).toLocaleString("en-CA")} <span className="text-muted-foreground">· {r.triggered_by}</span>
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="outline" className="text-[10px]">{r.stats.sources_checked} sources</Badge>
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">{r.stats.new_candidates} new</Badge>
                      <Badge variant="outline" className="text-[10px]">{r.stats.updated_candidates} updated</Badge>
                      <Badge variant="outline" className="text-[10px]">{r.stats.news_drafts} news drafts</Badge>
                      {r.errors.length > 0 && (
                        <Badge variant="outline" className="text-[10px] bg-red-50 text-red-800 border-red-200">{r.errors.length} errors</Badge>
                      )}
                    </div>
                  </div>
                  {r.errors.length > 0 && (
                    <ul className="mt-2 text-[11px] text-red-700 space-y-1">
                      {r.errors.map((e, i) => <li key={i}>{e.municipality}: {e.error}</li>)}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ))}
            {runs.length === 0 && (
              <Card><CardContent className="p-6 text-sm text-muted-foreground">
                No runs yet. The scheduler runs daily at 6:00 PM Eastern, or use “Run scan now”.
              </CardContent></Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
